import { pickClass, setActiveClass } from "@/lib/activeClass";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Trash2, MessageCircle, AlertTriangle, ThumbsUp, ThumbsDown, HeartHandshake,
  Award, ShieldAlert, Clock, User, Search, TrendingUp, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  retard:                { label: "Retard",                icon: Clock,         color: "#B45309", bg: "#FEF3C7" },
  comportement_positif:  { label: "Comportement +",        icon: ThumbsUp,      color: "#15803D", bg: "#DCFCE7" },
  comportement_negatif:  { label: "Comportement -",        icon: ThumbsDown,    color: "#B91C1C", bg: "#FEE2E2" },
  difficulte:            { label: "Difficulté",            icon: AlertTriangle, color: "#7C2D12", bg: "#FFEDD5" },
  recompense:            { label: "Récompense",            icon: Award,         color: "#166534", bg: "#DCFCE7" },
  sanction:              { label: "Sanction",              icon: ShieldAlert,   color: "#991B1B", bg: "#FECACA" },
  message_parent:        { label: "Message parent",        icon: MessageCircle, color: "#0F766E", bg: "#CCFBF1" },
};

const GRAVITE_LABEL = { leger: "Léger", moyen: "Moyen", grave: "Grave" };

const VALID_TABS = ["retards", "comportement", "difficultes", "recompenses", "sanctions", "fiche"];

const TAB_TO_TYPES = {
  retards: ["retard"],
  comportement: ["comportement_positif", "comportement_negatif"],
  difficultes: ["difficulte"],
  recompenses: ["recompense"],
  sanctions: ["sanction"],
};

const TAB_TITLES = {
  retards: "Retards",
  comportement: "Comportement",
  difficultes: "Difficultés",
  recompenses: "Récompenses",
  sanctions: "Sanctions",
  fiche: "Fiche de suivi",
};

const INDICE_COLORS = {
  green: { bg: "#DCFCE7", text: "#166534", border: "#86EFAC" },
  yellow: { bg: "#FEF9C3", text: "#854D0E", border: "#FDE047" },
  orange: { bg: "#FFEDD5", text: "#9A3412", border: "#FDBA74" },
  red: { bg: "#FEE2E2", text: "#991B1B", border: "#FCA5A5" },
};

export default function VieScolaire() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const initial = VALID_TABS.includes(urlTab) && urlTab !== "retards" ? urlTab : "comportement";
  const [tab, setTab] = useState(initial);

  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");

  useEffect(() => {
    api.get("/classes").then(({ data }) => {
      setClasses(data);
      if (data.length > 0) setClassId(pickClass(data));
    });
  }, []);

  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== tab) setTab(urlTab);
  }, [urlTab, tab]);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const canEdit = user?.role === "maitre";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
            Vie scolaire
          </h1>
          <p className="text-sm text-[#5C736A]">
            Retards, comportement, récompenses, sanctions et fiche de suivi par élève.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[#5C736A]">Classe</Label>
          <Select value={classId} onValueChange={(v) => { setClassId(v); setActiveClass(v); }}>
            <SelectTrigger className="w-44" data-testid="vs-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="w-full overflow-x-auto flex justify-start">
          <TabsTrigger value="comportement" data-testid="tab-comportement">Comportement</TabsTrigger>
          <TabsTrigger value="difficultes" data-testid="tab-difficultes">Difficultés</TabsTrigger>
          <TabsTrigger value="recompenses" data-testid="tab-recompenses">Récompenses</TabsTrigger>
          <TabsTrigger value="sanctions" data-testid="tab-sanctions">Sanctions</TabsTrigger>
          <TabsTrigger value="fiche" data-testid="tab-fiche">Fiche de suivi</TabsTrigger>
        </TabsList>

        {["comportement", "difficultes", "recompenses", "sanctions"].map((t) => (
          <TabsContent key={t} value={t} className="mt-5">
            <EventsTab
              tabKey={t}
              types={TAB_TO_TYPES[t]}
              classId={classId}
              canEdit={canEdit}
              title={TAB_TITLES[t]}
            />
          </TabsContent>
        ))}
        <TabsContent value="fiche" className="mt-5">
          <FicheTab classId={classId} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------------------------------------------------------
 * EVENTS TAB (generic for retards, comportement, difficultés, etc.)
 * ------------------------------------------------------------------ */
export function EventsTab({ tabKey, types, classId, canEdit, title }) {
  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [filterType, setFilterType] = useState(types[0]); // default subtype (for comportement)
  const [q, setQ] = useState("");

  const defaultForm = () => ({
    student_id: "",
    type: types[0],
    date: new Date().toISOString().slice(0, 10),
    description: "",
    categorie: "",
    gravite: types.includes("sanction") || types.includes("comportement_negatif") ? "leger" : undefined,
    duree_minutes: tabKey === "retards" ? 5 : undefined,
    points: tabKey === "recompenses" ? 2 : undefined,
    recommandations: "",
  });
  const [form, setForm] = useState(defaultForm());

  const load = async () => {
    if (!classId) { setStudents([]); setEvents([]); return; }
    setLoading(true);
    try {
      const [s, ...evLists] = await Promise.all([
        api.get("/students", { params: { class_id: classId } }),
        ...types.map((t) =>
          api.get("/vie-scolaire/events", { params: { class_id: classId, type: t } })
        ),
      ]);
      setStudents(s.data);
      const merged = [].concat(...evLists.map((r) => r.data));
      merged.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setEvents(merged);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [classId, tabKey]);

  const studentOf = (id) => students.find((s) => s.id === id);

  const filteredEvents = useMemo(() => {
    let list = events;
    if (types.length > 1 && filterType && filterType !== "all") {
      list = list.filter((e) => e.type === filterType);
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((e) => {
        const st = studentOf(e.student_id);
        const name = st ? `${st.prenom} ${st.nom}`.toLowerCase() : "";
        return name.includes(s)
          || (e.description || "").toLowerCase().includes(s)
          || (e.categorie || "").toLowerCase().includes(s);
      });
    }
    return list;
  }, [events, filterType, q, students, types]);

  const sortedStudents = useMemo(
    () => [...students].sort((a, b) =>
      (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
      || (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
    ),
    [students]
  );

  const create = async () => {
    if (!form.student_id) { toast.error("Sélectionnez un élève"); return; }
    try {
      const payload = { ...form };
      // clean empty optional fields
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      await api.post("/vie-scolaire/events", payload);
      toast.success(`${title} enregistré(e)`);
      setOpen(false);
      setForm(defaultForm());
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (ev) => {
    if (!confirm(`Supprimer cet événement (${TYPE_META[ev.type]?.label}) ?`)) return;
    try { await api.delete(`/vie-scolaire/events/${ev.id}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const waLink = (ev) => {
    const st = studentOf(ev.student_id);
    if (!st?.telephone_parent) return null;
    const tel = String(st.telephone_parent).replace(/\D/g, "");
    const number = tel.startsWith("221") ? tel : "221" + tel;
    const kind = TYPE_META[ev.type]?.label || "Événement";
    let msg = `Bonjour, concernant ${st.prenom} ${st.nom}, un événement « ${kind} » a été enregistré le ${ev.date}.`;
    if (ev.description) msg += ` Détail : ${ev.description}.`;
    return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  };

  // STATS mini
  const stats = useMemo(() => {
    const total = events.length;
    const byStudent = {};
    events.forEach((e) => { byStudent[e.student_id] = (byStudent[e.student_id] || 0) + 1; });
    const top = Object.entries(byStudent)
      .map(([sid, n]) => ({ student: studentOf(sid), count: n }))
      .filter((x) => x.student)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
    return { total, top };
  }, [events, students]);

  return (
    <div className="space-y-5">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={`Total ${title.toLowerCase()}`} value={stats.total} icon={TYPE_META[types[0]].icon} color={TYPE_META[types[0]].color} />
        {stats.top[0] && <KpiCard label="Élève #1" value={`${stats.top[0].student.prenom} ${stats.top[0].student.nom}`} sub={`${stats.top[0].count} fois`} icon={User} color="#1F7A63" />}
        {stats.top[1] && <KpiCard label="Élève #2" value={`${stats.top[1].student.prenom} ${stats.top[1].student.nom}`} sub={`${stats.top[1].count} fois`} icon={User} color="#5C736A" />}
        {stats.top[2] && <KpiCard label="Élève #3" value={`${stats.top[2].student.prenom} ${stats.top[2].student.nom}`} sub={`${stats.top[2].count} fois`} icon={User} color="#94A3B8" />}
      </div>

      {/* Actions & filters */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
            <Input
              className="pl-8"
              placeholder="Rechercher (nom, description…)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="vs-search"
            />
          </div>
          {types.length > 1 && (
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40" data-testid="vs-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {types.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(defaultForm()); }}>
            <Button
              className="bg-[#1F7A63] hover:bg-[#165c4b] text-white"
              onClick={() => setOpen(true)}
              data-testid="vs-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Nouveau
            </Button>
            <DialogContent>
              <DialogHeader><DialogTitle>Ajouter — {title}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Élève</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                    <SelectTrigger data-testid="vs-form-student"><SelectValue placeholder="Choisir un élève" /></SelectTrigger>
                    <SelectContent>
                      {sortedStudents.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {types.length > 1 && (
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {types.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                {tabKey === "retards" && (
                  <div>
                    <Label>Durée (minutes)</Label>
                    <Input type="number" min="1" value={form.duree_minutes || ""} onChange={(e) => setForm({ ...form, duree_minutes: Number(e.target.value) })} />
                  </div>
                )}
                {tabKey === "recompenses" && (
                  <div>
                    <Label>Points bonus (1 à 5)</Label>
                    <Input type="number" min="1" max="5" value={form.points || 2} onChange={(e) => setForm({ ...form, points: Number(e.target.value) })} />
                  </div>
                )}
                {(form.type === "sanction" || form.type === "comportement_negatif") && (
                  <div>
                    <Label>Gravité</Label>
                    <Select value={form.gravite} onValueChange={(v) => setForm({ ...form, gravite: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leger">Léger</SelectItem>
                        <SelectItem value="moyen">Moyen</SelectItem>
                        <SelectItem value="grave">Grave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Catégorie (optionnel)</Label>
                  <Input placeholder="ex : participation, bavardage…" value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                {(tabKey === "difficultes" || tabKey === "sanctions") && (
                  <div>
                    <Label>Recommandations (optionnel)</Label>
                    <Textarea rows={2} value={form.recommandations} onChange={(e) => setForm({ ...form, recommandations: e.target.value })} />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={create} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="vs-save-btn">
                  Enregistrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Table */}
      <div className="ek-card p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Élève</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="hidden sm:table-cell">Détail</TableHead>
              <TableHead className="hidden md:table-cell">Info</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEvents.map((ev) => {
              const st = studentOf(ev.student_id);
              const meta = TYPE_META[ev.type] || TYPE_META.difficulte;
              const Icon = meta.icon;
              return (
                <TableRow key={ev.id}>
                  <TableCell className="whitespace-nowrap">{ev.date}</TableCell>
                  <TableCell className="font-medium">{st ? `${st.prenom} ${st.nom}` : "—"}</TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
                      style={{ backgroundColor: meta.bg, color: meta.color }}
                    >
                      <Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell max-w-[240px] truncate" title={ev.description || ""}>
                    {ev.description || ev.categorie || "—"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-[#5C736A]">
                    {ev.type === "retard" && ev.duree_minutes != null && `${ev.duree_minutes} min`}
                    {ev.type === "recompense" && ev.points != null && `+${ev.points} pts`}
                    {ev.gravite && `Gravité : ${GRAVITE_LABEL[ev.gravite]}`}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {waLink(ev) && (
                        <a href={waLink(ev)} target="_blank" rel="noreferrer">
                          <Button size="icon" variant="ghost" title="Prévenir parent"><MessageCircle className="w-4 h-4 text-green-600" /></Button>
                        </a>
                      )}
                      {canEdit && (
                        <Button size="icon" variant="ghost" onClick={() => del(ev)} data-testid={`vs-del-${ev.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && filteredEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-sm text-[#5C736A]">
                  Aucun événement enregistré.
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-sm text-[#5C736A]">
                  Chargement…
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------
 * FICHE TAB — sélectionner un élève, voir son indice /100 et l'historique
 * ------------------------------------------------------------------ */
function FicheTab({ classId, canEdit }) {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [fiche, setFiche] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classId) { setStudents([]); return; }
    api.get("/students", { params: { class_id: classId } }).then(({ data }) => {
      const sorted = [...data].sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
        || (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
      );
      setStudents(sorted);
      if (sorted.length > 0) setStudentId(sorted[0].id);
      else setStudentId("");
    });
  }, [classId]);

  const load = async () => {
    if (!studentId) { setFiche(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/vie-scolaire/fiche/${studentId}`);
      setFiche(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [studentId]);

  const student = fiche?.student;
  const idx = fiche?.indice;
  const colorCfg = idx ? INDICE_COLORS[idx.color] || INDICE_COLORS.yellow : INDICE_COLORS.green;

  const events = fiche?.events || [];
  const absences = fiche?.absences || [];

  const sortedStudents = useMemo(
    () => [...students].sort((a, b) =>
      (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
      || (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
    ),
    [students]
  );

  const waLinkFiche = () => {
    const st = students.find((s) => s.id === studentId);
    if (!st?.telephone_parent) return null;
    const tel = String(st.telephone_parent).replace(/\D/g, "");
    const number = tel.startsWith("221") ? tel : "221" + tel;
    const msg = `Bonjour, concernant ${st.prenom} ${st.nom} : indice de comportement ${idx?.indice}/100 (${idx?.label}). Absences : ${idx?.counts?.absences || 0}, retards : ${idx?.counts?.retards || 0}, sanctions : ${idx?.counts?.sanctions || 0}.`;
    return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs text-[#5C736A]">Élève</Label>
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger className="w-64" data-testid="vs-fiche-student"><SelectValue placeholder="Choisir un élève" /></SelectTrigger>
          <SelectContent>
            {sortedStudents.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
          </SelectContent>
        </Select>
        {waLinkFiche() && (
          <a href={waLinkFiche()} target="_blank" rel="noreferrer">
            <Button variant="outline" className="border-green-500 text-green-700">
              <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp parent
            </Button>
          </a>
        )}
      </div>

      {loading && <div className="text-sm text-[#5C736A]">Chargement…</div>}

      {!loading && student && idx && (
        <>
          {/* Hero indice */}
          <div
            className="rounded-2xl p-5 sm:p-7 border-2"
            style={{ backgroundColor: colorCfg.bg, borderColor: colorCfg.border }}
          >
            <div className="flex flex-wrap items-center gap-6 justify-between">
              <div>
                <div className="text-sm font-medium" style={{ color: colorCfg.text }}>
                  {student.prenom} {student.nom} · {student.matricule}
                </div>
                <div className="mt-1 text-4xl sm:text-5xl font-bold" style={{ color: colorCfg.text, fontFamily: "Outfit" }}>
                  {idx.indice}<span className="text-xl">/100</span>
                </div>
                <div className="mt-1 text-lg font-semibold" style={{ color: colorCfg.text }}>{idx.label}</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <StatChip label="Absences" value={idx.counts.absences} color="#B45309" />
                <StatChip label="Retards" value={idx.counts.retards} color="#B45309" />
                <StatChip label="Sanctions" value={idx.counts.sanctions} color="#991B1B" />
                <StatChip label="Récompenses" value={idx.counts.recompenses} color="#166534" sub={`+${idx.counts.recompenses_points} pts`} />
              </div>
            </div>
            <div className="mt-3 text-xs" style={{ color: colorCfg.text }}>
              Formule : 100 − (absences × 2 + retards × 1 + sanctions × 5) + points récompenses (max +30)
            </div>
          </div>

          {/* Répartition */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {["comportement_positif", "comportement_negatif", "difficulte", "message_parent"].map((t) => {
              const count = events.filter((e) => e.type === t).length;
              const meta = TYPE_META[t];
              const Icon = meta.icon;
              return (
                <div key={t} className="ek-card p-4">
                  <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: meta.color }}>
                    <Icon className="w-4 h-4" /> {meta.label}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-[#1A2E28]">{count}</div>
                </div>
              );
            })}
          </div>

          {/* Historique unifié */}
          <div className="ek-card p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="hidden sm:table-cell">Info</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((ev) => {
                  const meta = TYPE_META[ev.type] || TYPE_META.difficulte;
                  const Icon = meta.icon;
                  return (
                    <TableRow key={ev.id}>
                      <TableCell className="whitespace-nowrap">{ev.date}</TableCell>
                      <TableCell>
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
                          style={{ backgroundColor: meta.bg, color: meta.color }}
                        >
                          <Icon className="w-3 h-3" />
                          {meta.label}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[280px] truncate" title={ev.description || ""}>
                        {ev.description || ev.categorie || "—"}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-xs text-[#5C736A]">
                        {ev.type === "retard" && ev.duree_minutes != null && `${ev.duree_minutes} min`}
                        {ev.type === "recompense" && ev.points != null && `+${ev.points} pts`}
                        {ev.gravite && `Gravité : ${GRAVITE_LABEL[ev.gravite]}`}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {absences.map((a) => (
                  <TableRow key={`abs-${a.id}`}>
                    <TableCell className="whitespace-nowrap">{a.date}</TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
                        style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}
                      >
                        <TrendingDown className="w-3 h-3" />
                        Absence
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate">
                      {a.motif || "—"} {a.justifiee ? "(justifiée)" : ""}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-[#5C736A] capitalize">
                      {a.demi_journee}
                    </TableCell>
                  </TableRow>
                ))}
                {events.length === 0 && absences.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-sm text-[#5C736A]">
                      Aucun événement enregistré pour cet élève.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------
 * Reusable small cards
 * ------------------------------------------------------------------ */
function KpiCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="ek-card p-4">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#5C736A]">
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
        {label}
      </div>
      <div className="mt-1 text-xl font-bold text-[#1A2E28] truncate" title={String(value)}>{value}</div>
      {sub && <div className="text-xs text-[#5C736A]">{sub}</div>}
    </div>
  );
}

function StatChip({ label, value, sub, color = "#1A2E28" }) {
  return (
    <div className="bg-white/70 rounded-xl px-3 py-2 min-w-[70px]">
      <div className="text-[10px] uppercase tracking-wide text-[#5C736A]">{label}</div>
      <div className="text-lg font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-[#5C736A]">{sub}</div>}
    </div>
  );
}
